<template>
    <div v-if="publishStatus" class="account-status">
        {{ publishStatusLabel }}

        <Poptip v-if="publishSummary.length" trigger="hover">
            <Icon type="md-information-circle" />
            <template #content>
                <ul>
                    <li v-for="(infoSummary, index) in publishSummary" :key="index" class="info">
                        <span class="info-name">{{ infoSummary.key }}</span>
                        <span>{{ infoSummary.value }}</span>
                    </li>
                </ul>

                <Button
                    v-if="publishStatus === 'DONE_WITH_ERRORS'"
                    class="detailed-button"
                    :disabled="isDetailedPublishingStatusUpdating"
                    :loading="isDetailedPublishingStatusUpdating"
                    data-testid="connect__show-more-details"
                    @click="getPublishDetails"
                >
                    Show more details
                </Button>
            </template>
        </Poptip>

        <hox-modal v-if="showDetailedPublishedStatusModal" @close="showDetailedPublishedStatusModal = false">
            <template #header>Detailed Publish Status</template>
            <div>
                {{ publishDetails }}
            </div>
        </hox-modal>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";

import type { PlatformAccount } from "@/Connect/types";
import { useConnectStore } from "@/Connect/store";

import { getAccountPublishDetails } from "@/utils/CreativeIntelligenceApi/connect";

import snackbar from "@/utils/snackbar";

const { platformAccount, campaignId } = defineProps<{ platformAccount: PlatformAccount; campaignId: string }>();

const publishDetails = ref("");
const isDetailedPublishingStatusUpdating = ref(false);
const showDetailedPublishedStatusModal = ref(false);

const connectStore = useConnectStore();

const publishStatus = computed(() => {
    return platformAccount.id ? connectStore.publishSummary[platformAccount.id]?.status : "";
});

const publishSummary = computed(() => {
    return platformAccount.id ? connectStore.publishSummary[platformAccount.id]?.summary : [];
});

const publishStatusLabel = computed(() => {
    switch (publishStatus.value) {
        case "DONE": {
            if (publishSummary.value.length === 0) {
                return "No assets or ads to publish";
            }
            return "Published";
        }
        case "IN_PROGRESS":
            return "Publishing...";
        case "DONE_WITH_ERRORS":
            return "Published with errors";
        default:
            return "";
    }
});

const apiInfo = computed(() => connectStore.apiInfo);

async function getPublishDetails() {
    if (!platformAccount.id || !apiInfo.value) {
        return;
    }

    try {
        isDetailedPublishingStatusUpdating.value = true;

        const { data } = await getAccountPublishDetails({
            baseUrl: apiInfo.value.url,
            campaignId,
            platformAccountId: platformAccount.id
        });

        if (!data.length) {
            snackbar.error("No details data found.");
        } else if (data[0]?.error?.message) {
            showDetailedPublishedStatusModal.value = true;
            publishDetails.value = data[0].error.message;
        }
    } catch (e) {
        const errorMessage = (e as Error).message ?? "There was an error please try again.";

        snackbar.error(errorMessage);
    }

    isDetailedPublishingStatusUpdating.value = false;
}
</script>

<style lang="scss" scoped>
.account-status {
    display: flex;
    gap: 5px;
    padding: 0 10px;
    font-weight: 400;
    font-size: 16px;

    td {
        padding: 0 5px;
    }
}

.info {
    font-weight: 300;
    display: flex;
    margin-left: 10px;
    span {
        display: block;
    }
}

.info-name {
    margin-right: auto;
    padding-right: 20px;
}

.detailed-button {
    margin-top: 10px;
    height: 40px;

    > span {
        display: flex;
        align-items: center;
    }

    .loading-spinner {
        margin-top: 10px;
    }
}
</style>
